// What a `help_request.types[].payload_schema` accepts, read the way the server
// reads it.
//
// It lives beside the schema's own editor rather than inside it because the
// caller is somewhere else entirely: the Bots page checks the payload TEMPLATES
// an operator typed against the schema of the type they named, which is the same
// question `POST /v1/guilds/{id}/help-requests` asks of a game's own payload.
// One rule, one wording, two pages.
//
// The rules mirror `internal/platform/card`'s payload catalogue, and every
// asymmetry below is one of its decisions rather than this file's:
//
//   - a type with NO schema accepts anything at all, including no payload;
//   - a declared schema needs an object, because a scalar has no keys to judge;
//   - an explicit null is not a value: under a declared key it reads as the key
//     being absent, which is what makes it fail `required`;
//   - a whole number written as a string is not an `int` here, though it is on
//     a player's card — see `card.Catalogue.strictInt`;
//   - the allow-list applies to the string-ish types only, so a list left on an
//     `int` field by a hand-edited config restricts nothing.
//
// `internal/help/testdata/payload_cases.json` is the shared table both this and
// the Go check are run over, so a divergence fails a test rather than being
// noticed by somebody reading two files side by side.

import type {
  MetaFieldType,
  MetaSchemaField,
} from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import { supportsAllowList } from "../../../components/MetaSchemaEditor/metaSchema";

/**
 * The four ways a payload can fail a schema, plus the one that fails before any
 * key is looked at. The names are the server's own — they are the `reason` of
 * the `help_invalid_payload` details — so the two halves of the platform cannot
 * end up calling the same refusal two things.
 */
export type PayloadProblemReason =
  "required" | "not_declared" | "not_allowed" | "wrong_type" | "not_an_object";

export interface PayloadProblem {
  /** The key at fault; absent for `not_an_object`, where no key is. */
  key?: string;
  reason: PayloadProblemReason;
}

/** A JSON object and not an array — the shape a payload has to be. */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Whether a value matches one declared type. `undefined` never reaches here:
 *  a key that is not in the object is the caller's `required` question. */
function matchesType(value: unknown, type: MetaFieldType): boolean {
  switch (type) {
    case "string":
      return typeof value === "string";
    case "string[]":
      return Array.isArray(value) && value.every((v) => typeof v === "string");
    case "int":
      // A quoted number is NOT an int, deliberately: see the header. Nor is a
      // whole number too large to be one — the server reads it as an int64 and
      // refuses what will not fit, and past 2^53 JavaScript cannot even tell
      // two of them apart. A template saved here and refused there is the one
      // outcome this check exists to prevent.
      return typeof value === "number" && Number.isSafeInteger(value);
    case "bool":
      return typeof value === "boolean";
  }
}

/** Whether a value passes the field's allow-list. An empty or absent list does
 *  not restrict anything, and for a `string[]` every element is checked.
 *
 *  A list on an `int` or a `bool` restricts nothing either: only the string-ish
 *  types carry one, and the server agrees — `card.Field.accepts` consults
 *  `allowed` in those two branches and nowhere else. A hand-edited config can
 *  leave one behind on a field whose type has since changed, and reading it
 *  would refuse a value the server takes. */
function allowed(value: unknown, field: MetaSchemaField): boolean {
  if (!supportsAllowList(field.type)) return true;
  const list = field.allowed ?? [];
  if (list.length === 0) return true;
  if (Array.isArray(value))
    return value.every((v) => list.includes(v as string));
  return list.includes(value as string);
}

/**
 * Every way this payload fails this schema, in key order — the same order and
 * the same completeness the server's 400 carries, so a template with three
 * things wrong with it says all three at once.
 *
 * A schema that is absent or empty answers nothing at all: that type accepts
 * any payload, which is the state of every type of every project until a game
 * designer declares the first field.
 */
export function checkPayload(
  payload: unknown,
  schema: MetaSchemaField[] | undefined,
): PayloadProblem[] {
  if (!schema || schema.length === 0) return [];
  if (!isPlainObject(payload)) return [{ reason: "not_an_object" }];

  const problems: PayloadProblem[] = [];
  const byKey = new Map(schema.map((f) => [f.key, f]));
  // Own properties only. `payload.constructor` is a function on every object
  // JSON.parse returns, and `constructor` is a key `SCHEMA_KEY_PATTERN` admits,
  // so reading the value would find one where the payload carries none — and
  // the panel would pass a template the server, which walks a map, refuses.
  const has = (key: string) => Object.hasOwn(payload, key);
  const asked = new Set<string>();
  for (const field of schema) {
    // A key declared twice is one missing key, as it is on the server: the
    // list is read by a person, and saying it twice helps nobody.
    if (!field.required || asked.has(field.key)) continue;
    if (!has(field.key) || payload[field.key] === null) {
      asked.add(field.key);
      problems.push({ key: field.key, reason: "required" });
    }
  }
  for (const key of Object.keys(payload)) {
    const field = byKey.get(key);
    if (!field) {
      problems.push({ key, reason: "not_declared" });
      continue;
    }
    // A null under a declared key is not a value — the `required` pass above
    // has already had its say about it.
    const v = payload[key];
    if (v === null) continue;
    if (!matchesType(v, field.type)) {
      problems.push({ key, reason: "wrong_type" });
      continue;
    }
    if (!allowed(v, field)) problems.push({ key, reason: "not_allowed" });
  }
  // Byte order, not the locale's: the server sorts with `strings.Compare`, and
  // `localeCompare` puts `_x` before `1x` where Go puts it after. The two lists
  // must be able to be compared element by element.
  return problems.sort((a, b) => {
    const x = a.key ?? "";
    const y = b.key ?? "";
    return x < y ? -1 : x > y ? 1 : 0;
  });
}

/**
 * One problem in words — the same three sentences the mockup writes under a
 * template row, which are the same three refusals the server answers a game
 * with.
 *
 * `schema` is only read to name a field's declared type, so the wrong-type line
 * can say what the value should have been.
 */
export function payloadProblemText(
  problem: PayloadProblem,
  schema: MetaSchemaField[] | undefined,
): string {
  switch (problem.reason) {
    case "not_an_object":
      return "Payload must be a JSON object";
    case "required":
      return `${problem.key} is required by this type's schema`;
    case "not_declared":
      return `${problem.key} is not declared by this type's schema`;
    case "not_allowed":
      return `${problem.key}: value is not in the allow-list`;
    case "wrong_type": {
      const type = schema?.find((f) => f.key === problem.key)?.type;
      return type
        ? `${problem.key}: value is not a valid ${type}`
        : `${problem.key}: value is not what the schema declares`;
    }
  }
}

/**
 * The keys a schema requires, in the order it declares them.
 *
 * The Bots page asks it twice over: an empty answer means a request carrying no
 * payload satisfies this type, which is what a bot asks with when its kind has
 * no template to draw; a non-empty one is both the warning's trigger and the
 * list of keys it names. */
export function requiredKeys(schema: MetaSchemaField[] | undefined): string[] {
  return (schema ?? []).filter((f) => f.required).map((f) => f.key);
}
